import React, { useState } from "react";
import { getThemeColors, Theme } from "../theme/colors";

interface JsonTreeProps {
  data: unknown;
  theme: Theme;
  username: string;
  level?: number;
  isRoot?: boolean;
}

const JsonTree: React.FC<JsonTreeProps> = ({
  data,
  theme,
  username,
  level = 0,
  isRoot = true,
}) => {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const colors = getThemeColors(username, theme);

  const toggleExpanded = (key: string) => {
    setExpanded((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const renderValue = (
    value: unknown,
    key?: string | number
  ): React.ReactNode => {
    if (value === null) {
      return (
        <span style={{ color: colors.text, fontStyle: "italic" }}>null</span>
      );
    }

    if (value === undefined) {
      return (
        <span style={{ color: colors.text, fontStyle: "italic" }}>
          undefined
        </span>
      );
    }

    if (typeof value === "boolean") {
      return <span style={{ color: "#ff6b6b" }}>{value.toString()}</span>;
    }

    if (typeof value === "number") {
      return <span style={{ color: "#4ecdc4" }}>{value}</span>;
    }

    if (typeof value === "string") {
      return <span style={{ color: "#45b7d1" }}>"{value}"</span>;
    }

    if (Array.isArray(value)) {
      const arrayKey = key ? `${key}` : `array-${level}`;
      const isExpanded = expanded[arrayKey] ?? false;

      return (
        <div style={{ marginLeft: level * 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => toggleExpanded(arrayKey)}
          >
            <span style={{ marginRight: "8px", color: colors.text }}>
              {isExpanded ? "▼" : "▶"}
            </span>
            <span style={{ color: colors.text }}>Array ({value.length})</span>
          </div>
          {isExpanded && (
            <div style={{ marginLeft: 20 }}>
              {value.map((item, index) => (
                <div key={index} style={{ marginBottom: "4px" }}>
                  <span style={{ color: colors.text, marginRight: "8px" }}>
                    [{index}]:
                  </span>
                  {renderValue(item, index)}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (typeof value === "object" && value !== null) {
      const objectKey = key ? `${key}` : `object-${level}`;
      const isExpanded = expanded[objectKey] ?? false;
      const keys = Object.keys(value as Record<string, unknown>);

      return (
        <div style={{ marginLeft: level * 20 }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              cursor: "pointer",
              userSelect: "none",
            }}
            onClick={() => toggleExpanded(objectKey)}
          >
            <span style={{ marginRight: "8px", color: colors.text }}>
              {isExpanded ? "▼" : "▶"}
            </span>
            <span style={{ color: colors.text }}>
              Object {keys.length > 0 && `(${keys.length} properties)`}
            </span>
          </div>
          {isExpanded && (
            <div style={{ marginLeft: 20 }}>
              {keys.map((objKey) => (
                <div key={objKey} style={{ marginBottom: "4px" }}>
                  <span style={{ color: "#f39c12", marginRight: "8px" }}>
                    {objKey}:
                  </span>
                  {renderValue(
                    (value as Record<string, unknown>)[objKey],
                    objKey
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    return <span style={{ color: colors.text }}>{String(value)}</span>;
  };

  if (isRoot) {
    return (
      <div
        style={{
          backgroundColor: colors.optionsBg,
          border: `1px solid ${colors.border}`,
          borderRadius: "8px",
          padding: "12px",
          marginTop: "8px",
          fontFamily: "monospace",
          fontSize: "13px",
          maxHeight: "400px",
          overflowY: "auto",
        }}
      >
        {renderValue(data)}
      </div>
    );
  }

  return <>{renderValue(data)}</>;
};

export default JsonTree;
